/**
 * Consent copy, one entry per live marketing locale (DIT-402).
 *
 * Deliberately NOT in either i18n system. The landing runs two of them — the
 * marketing instances (flat keys, per-locale i18next instances) and the funnel
 * provider (nested keys, its own bundle directories) — and the consent banner is
 * initialised once outside React, before either is mounted, so it can belong to
 * neither. The library takes its own translations map keyed by language code,
 * which is exactly the shape below.
 *
 * The category descriptions say what a category is FOR, never whether we happen
 * to use it today. There are no analytics or marketing scripts on the site at the
 * moment, but writing that into the copy would silently go stale the day one is
 * added, in 13 languages at once.
 */

export interface ConsentCopy {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  manage: string;
  prefTitle: string;
  save: string;
  close: string;
  introTitle: string;
  introBody: string;
  necessaryTitle: string;
  necessaryBody: string;
  analyticsTitle: string;
  analyticsBody: string;
  marketingTitle: string;
  marketingBody: string;
  preferencesTitle: string;
  preferencesBody: string;
  colName: string;
  colPurpose: string;
  colExpiry: string;
  localeCookie: string;
  consentCookie: string;
  expiry12m: string;
  expiry6m: string;
  signupState: string;
  expiryStorage: string;
}

export const CONSENT_COPY: Record<string, ConsentCopy> = {
  en: {
    title: 'We use cookies',
    description:
      'Some cookies are needed for the site to work. With your permission we would also use them to understand how the site is used and to measure our marketing.',
    acceptAll: 'Accept all',
    rejectAll: 'Reject non-essential',
    manage: 'Manage preferences',
    prefTitle: 'Cookie preferences',
    save: 'Save preferences',
    close: 'Close',
    introTitle: 'How we use cookies',
    introBody:
      'Necessary cookies are always on, because the site cannot work without them. Everything else stays off until you turn it on, and you can change your choice at any time.',
    necessaryTitle: 'Strictly necessary',
    necessaryBody:
      'Remember the language you chose, keep the signup you are completing, and store your cookie choice. These cannot be switched off.',
    analyticsTitle: 'Analytics',
    analyticsBody:
      'Help us see which pages are used and where visitors get stuck, so we can improve them.',
    marketingTitle: 'Marketing',
    marketingBody: 'Used to measure our advertising and to show content that is relevant to you.',
    preferencesTitle: 'Preferences',
    preferencesBody:
      'Remember optional choices you make, so the site behaves the way you left it.',
    colName: 'Name',
    colPurpose: 'Purpose',
    colExpiry: 'Expiry',
    localeCookie: 'The language you chose for the site.',
    consentCookie: 'Your cookie choices.',
    expiry12m: '12 months',
    expiry6m: '6 months',
    signupState: 'The signup you are part-way through.',
    expiryStorage: 'Local storage, until cleared',
  },

  nl: {
    title: 'We gebruiken cookies',
    description:
      'Sommige cookies zijn nodig om de site te laten werken. Met uw toestemming gebruiken we ze ook om te begrijpen hoe de site wordt gebruikt en om onze marketing te meten.',
    acceptAll: 'Alles accepteren',
    rejectAll: 'Niet-essentiële weigeren',
    manage: 'Voorkeuren beheren',
    prefTitle: 'Cookievoorkeuren',
    save: 'Voorkeuren opslaan',
    close: 'Sluiten',
    introTitle: 'Hoe we cookies gebruiken',
    introBody:
      'Noodzakelijke cookies staan altijd aan, omdat de site zonder deze niet werkt. Al het andere blijft uit tot u het aanzet, en u kunt uw keuze altijd wijzigen.',
    necessaryTitle: 'Strikt noodzakelijk',
    necessaryBody:
      'Onthouden welke taal u koos, de registratie die u aan het invullen bent bewaren, en uw cookiekeuze opslaan. Deze kunnen niet worden uitgeschakeld.',
    analyticsTitle: 'Analytisch',
    analyticsBody:
      'Helpen ons te zien welke pagina’s worden gebruikt en waar bezoekers vastlopen, zodat we ze kunnen verbeteren.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Gebruikt om onze advertenties te meten en om inhoud te tonen die voor u relevant is.',
    preferencesTitle: 'Voorkeuren',
    preferencesBody:
      'Onthouden optionele keuzes die u maakt, zodat de site werkt zoals u hem achterliet.',
    colName: 'Naam',
    colPurpose: 'Doel',
    colExpiry: 'Vervalt',
    localeCookie: 'De taal die u voor de site koos.',
    consentCookie: 'Uw cookiekeuzes.',
    expiry12m: '12 maanden',
    expiry6m: '6 maanden',
    signupState: 'De registratie die u aan het afronden bent.',
    expiryStorage: 'Lokale opslag, tot u het wist',
  },

  'fr-be': {
    title: 'Nous utilisons des cookies',
    description:
      'Certains cookies sont nécessaires au fonctionnement du site. Avec votre accord, nous les utiliserions aussi pour comprendre comment le site est utilisé et pour mesurer notre marketing.',
    acceptAll: 'Tout accepter',
    rejectAll: 'Refuser les non essentiels',
    manage: 'Gérer les préférences',
    prefTitle: 'Préférences de cookies',
    save: 'Enregistrer les préférences',
    close: 'Fermer',
    introTitle: 'Comment nous utilisons les cookies',
    introBody:
      'Les cookies nécessaires sont toujours actifs, car le site ne peut pas fonctionner sans eux. Tout le reste reste désactivé tant que vous ne l’activez pas, et vous pouvez modifier votre choix à tout moment.',
    necessaryTitle: 'Strictement nécessaires',
    necessaryBody:
      'Mémorisent la langue choisie, conservent l’inscription en cours et enregistrent votre choix de cookies. Ils ne peuvent pas être désactivés.',
    analyticsTitle: 'Analytiques',
    analyticsBody:
      'Nous aident à voir quelles pages sont utilisées et où les visiteurs bloquent, afin de les améliorer.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Utilisés pour mesurer notre publicité et afficher du contenu qui vous concerne.',
    preferencesTitle: 'Préférences',
    preferencesBody:
      'Mémorisent les choix facultatifs que vous faites, afin que le site reste comme vous l’avez laissé.',
    colName: 'Nom',
    colPurpose: 'Finalité',
    colExpiry: 'Expiration',
    localeCookie: 'La langue que vous avez choisie pour le site.',
    consentCookie: 'Vos choix de cookies.',
    expiry12m: '12 mois',
    expiry6m: '6 mois',
    signupState: "L'inscription que vous êtes en train de compléter.",
    expiryStorage: "Stockage local, jusqu'à effacement",
  },

  de: {
    title: 'Wir verwenden Cookies',
    description:
      'Einige Cookies sind für den Betrieb der Website erforderlich. Mit Ihrer Erlaubnis würden wir sie auch verwenden, um zu verstehen, wie die Website genutzt wird, und um unser Marketing zu messen.',
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Nicht notwendige ablehnen',
    manage: 'Einstellungen verwalten',
    prefTitle: 'Cookie-Einstellungen',
    save: 'Einstellungen speichern',
    close: 'Schließen',
    introTitle: 'Wie wir Cookies verwenden',
    introBody:
      'Notwendige Cookies sind immer aktiv, weil die Website ohne sie nicht funktioniert. Alles andere bleibt aus, bis Sie es einschalten, und Sie können Ihre Wahl jederzeit ändern.',
    necessaryTitle: 'Unbedingt erforderlich',
    necessaryBody:
      'Merken sich die gewählte Sprache, bewahren die laufende Registrierung und speichern Ihre Cookie-Entscheidung. Sie können nicht deaktiviert werden.',
    analyticsTitle: 'Statistik',
    analyticsBody:
      'Helfen uns zu sehen, welche Seiten genutzt werden und wo Besucher hängen bleiben, damit wir sie verbessern können.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Dienen dazu, unsere Werbung zu messen und Inhalte anzuzeigen, die für Sie relevant sind.',
    preferencesTitle: 'Präferenzen',
    preferencesBody:
      'Merken sich optionale Einstellungen, damit die Website so bleibt, wie Sie sie verlassen haben.',
    colName: 'Name',
    colPurpose: 'Zweck',
    colExpiry: 'Ablauf',
    localeCookie: 'Die von Ihnen gewählte Sprache der Website.',
    consentCookie: 'Ihre Cookie-Entscheidungen.',
    expiry12m: '12 Monate',
    expiry6m: '6 Monate',
    signupState: 'Die Registrierung, die Sie gerade abschließen.',
    expiryStorage: 'Lokaler Speicher, bis Sie ihn löschen',
  },

  es: {
    title: 'Usamos cookies',
    description:
      'Algunas cookies son necesarias para que el sitio funcione. Con su permiso, también las usaríamos para entender cómo se usa el sitio y para medir nuestro marketing.',
    acceptAll: 'Aceptar todo',
    rejectAll: 'Rechazar las no esenciales',
    manage: 'Gestionar preferencias',
    prefTitle: 'Preferencias de cookies',
    save: 'Guardar preferencias',
    close: 'Cerrar',
    introTitle: 'Cómo usamos las cookies',
    introBody:
      'Las cookies necesarias están siempre activas, porque el sitio no puede funcionar sin ellas. Todo lo demás permanece desactivado hasta que usted lo active, y puede cambiar su elección en cualquier momento.',
    necessaryTitle: 'Estrictamente necesarias',
    necessaryBody:
      'Recuerdan el idioma elegido, conservan el registro que está completando y guardan su elección de cookies. No se pueden desactivar.',
    analyticsTitle: 'Analíticas',
    analyticsBody:
      'Nos ayudan a ver qué páginas se usan y dónde se atascan los visitantes, para poder mejorarlas.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Se usan para medir nuestra publicidad y mostrar contenido que sea relevante para usted.',
    preferencesTitle: 'Preferencias',
    preferencesBody:
      'Recuerdan las opciones que elija, para que el sitio se comporte como lo dejó.',
    colName: 'Nombre',
    colPurpose: 'Finalidad',
    colExpiry: 'Caducidad',
    localeCookie: 'El idioma que eligió para el sitio.',
    consentCookie: 'Sus preferencias de cookies.',
    expiry12m: '12 meses',
    expiry6m: '6 meses',
    signupState: 'El registro que está completando.',
    expiryStorage: 'Almacenamiento local, hasta que lo borre',
  },

  lt: {
    title: 'Naudojame slapukus',
    description:
      'Kai kurie slapukai būtini, kad svetainė veiktų. Gavę jūsų sutikimą, juos naudotume ir tam, kad suprastume, kaip svetainė naudojama, ir įvertintume savo rinkodarą.',
    acceptAll: 'Priimti visus',
    rejectAll: 'Atmesti nebūtinus',
    manage: 'Tvarkyti nustatymus',
    prefTitle: 'Slapukų nustatymai',
    save: 'Išsaugoti nustatymus',
    close: 'Uždaryti',
    introTitle: 'Kaip naudojame slapukus',
    introBody:
      'Būtini slapukai visada įjungti, nes be jų svetainė neveikia. Visa kita lieka išjungta, kol patys neįjungsite, o savo pasirinkimą galite pakeisti bet kada.',
    necessaryTitle: 'Būtinieji',
    necessaryBody:
      'Įsimena pasirinktą kalbą, išsaugo pradėtą registraciją ir jūsų sprendimą dėl slapukų. Jų išjungti negalima.',
    analyticsTitle: 'Analitiniai',
    analyticsBody:
      'Padeda matyti, kurie puslapiai naudojami ir kur lankytojai užstringa, kad galėtume juos patobulinti.',
    marketingTitle: 'Rinkodaros',
    marketingBody: 'Naudojami mūsų reklamai įvertinti ir jums aktualiam turiniui rodyti.',
    preferencesTitle: 'Nuostatos',
    preferencesBody:
      'Įsimena pasirenkamus nustatymus, kad svetainė veiktų taip, kaip ją palikote.',
    colName: 'Pavadinimas',
    colPurpose: 'Paskirtis',
    colExpiry: 'Galiojimas',
    localeCookie: 'Jūsų pasirinkta svetainės kalba.',
    consentCookie: 'Jūsų sprendimai dėl slapukų.',
    expiry12m: '12 mėnesių',
    expiry6m: '6 mėnesiai',
    signupState: 'Registracija, kurią šiuo metu pildote.',
    expiryStorage: 'Vietinė saugykla, kol ištrinsite',
  },

  it: {
    title: 'Usiamo i cookie',
    description:
      'Alcuni cookie sono necessari al funzionamento del sito. Con il suo consenso li useremmo anche per capire come viene usato il sito e per misurare il nostro marketing.',
    acceptAll: 'Accetta tutti',
    rejectAll: 'Rifiuta i non essenziali',
    manage: 'Gestisci preferenze',
    prefTitle: 'Preferenze sui cookie',
    save: 'Salva preferenze',
    close: 'Chiudi',
    introTitle: 'Come usiamo i cookie',
    introBody:
      'I cookie necessari sono sempre attivi, perché senza di essi il sito non funziona. Tutto il resto resta disattivato finché non lo attiva lei, e può cambiare scelta in qualsiasi momento.',
    necessaryTitle: 'Strettamente necessari',
    necessaryBody:
      'Ricordano la lingua scelta, conservano la registrazione in corso e salvano la sua scelta sui cookie. Non possono essere disattivati.',
    analyticsTitle: 'Analitici',
    analyticsBody:
      'Ci aiutano a vedere quali pagine vengono usate e dove i visitatori si bloccano, per poterle migliorare.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Servono a misurare la nostra pubblicità e a mostrare contenuti rilevanti per lei.',
    preferencesTitle: 'Preferenze',
    preferencesBody:
      'Ricordano le scelte facoltative che effettua, così il sito resta come l’ha lasciato.',
    colName: 'Nome',
    colPurpose: 'Finalità',
    colExpiry: 'Scadenza',
    localeCookie: 'La lingua che ha scelto per il sito.',
    consentCookie: 'Le sue scelte sui cookie.',
    expiry12m: '12 mesi',
    expiry6m: '6 mesi',
    signupState: 'La registrazione che sta completando.',
    expiryStorage: 'Archiviazione locale, fino alla cancellazione',
  },

  pt: {
    title: 'Utilizamos cookies',
    description:
      'Alguns cookies são necessários para o funcionamento do site. Com a sua autorização, também os usaríamos para perceber como o site é utilizado e para medir o nosso marketing.',
    acceptAll: 'Aceitar tudo',
    rejectAll: 'Recusar os não essenciais',
    manage: 'Gerir preferências',
    prefTitle: 'Preferências de cookies',
    save: 'Guardar preferências',
    close: 'Fechar',
    introTitle: 'Como utilizamos os cookies',
    introBody:
      'Os cookies necessários estão sempre ativos, porque o site não funciona sem eles. Tudo o resto fica desativado até que os ative, e pode alterar a sua escolha a qualquer momento.',
    necessaryTitle: 'Estritamente necessários',
    necessaryBody:
      'Guardam o idioma escolhido, mantêm o registo que está a concluir e guardam a sua escolha de cookies. Não podem ser desativados.',
    analyticsTitle: 'Analíticos',
    analyticsBody:
      'Ajudam-nos a ver que páginas são usadas e onde os visitantes ficam presos, para as podermos melhorar.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Usados para medir a nossa publicidade e mostrar conteúdos relevantes para si.',
    preferencesTitle: 'Preferências',
    preferencesBody:
      'Guardam as escolhas opcionais que faz, para que o site fique como o deixou.',
    colName: 'Nome',
    colPurpose: 'Finalidade',
    colExpiry: 'Validade',
    localeCookie: 'O idioma que escolheu para o site.',
    consentCookie: 'As suas escolhas de cookies.',
    expiry12m: '12 meses',
    expiry6m: '6 meses',
    signupState: 'O registo que está a concluir.',
    expiryStorage: 'Armazenamento local, até que o limpe',
  },

  pl: {
    title: 'Używamy plików cookie',
    description:
      'Niektóre pliki cookie są niezbędne do działania strony. Za Twoją zgodą używalibyśmy ich również, aby rozumieć, jak strona jest używana, i mierzyć nasz marketing.',
    acceptAll: 'Zaakceptuj wszystkie',
    rejectAll: 'Odrzuć zbędne',
    manage: 'Zarządzaj ustawieniami',
    prefTitle: 'Ustawienia plików cookie',
    save: 'Zapisz ustawienia',
    close: 'Zamknij',
    introTitle: 'Jak używamy plików cookie',
    introBody:
      'Niezbędne pliki cookie są zawsze włączone, ponieważ bez nich strona nie działa. Wszystko inne pozostaje wyłączone, dopóki tego nie włączysz, a swój wybór możesz zmienić w każdej chwili.',
    necessaryTitle: 'Niezbędne',
    necessaryBody:
      'Zapamiętują wybrany język, zachowują rozpoczętą rejestrację i przechowują Twój wybór dotyczący plików cookie. Nie można ich wyłączyć.',
    analyticsTitle: 'Analityczne',
    analyticsBody:
      'Pomagają nam zobaczyć, które strony są używane i gdzie odwiedzający się zatrzymują, abyśmy mogli je ulepszyć.',
    marketingTitle: 'Marketingowe',
    marketingBody:
      'Służą do mierzenia naszych reklam i pokazywania treści istotnych dla Ciebie.',
    preferencesTitle: 'Preferencje',
    preferencesBody:
      'Zapamiętują opcjonalne wybory, aby strona działała tak, jak ją zostawiłeś.',
    colName: 'Nazwa',
    colPurpose: 'Cel',
    colExpiry: 'Wygasa',
    localeCookie: 'Wybrany przez Ciebie język strony.',
    consentCookie: 'Twoje wybory dotyczące plików cookie.',
    expiry12m: '12 miesięcy',
    expiry6m: '6 miesięcy',
    signupState: 'Rejestracja, którą właśnie wypełniasz.',
    expiryStorage: 'Pamięć lokalna, do wyczyszczenia',
  },

  ro: {
    title: 'Folosim module cookie',
    description:
      'Unele module cookie sunt necesare pentru funcționarea site-ului. Cu permisiunea dumneavoastră le-am folosi și pentru a înțelege cum este utilizat site-ul și pentru a măsura marketingul nostru.',
    acceptAll: 'Acceptă toate',
    rejectAll: 'Respinge cele neesențiale',
    manage: 'Gestionează preferințele',
    prefTitle: 'Preferințe pentru cookie-uri',
    save: 'Salvează preferințele',
    close: 'Închide',
    introTitle: 'Cum folosim modulele cookie',
    introBody:
      'Modulele cookie necesare sunt mereu active, deoarece site-ul nu poate funcționa fără ele. Restul rămâne dezactivat până când le activați, iar alegerea poate fi schimbată oricând.',
    necessaryTitle: 'Strict necesare',
    necessaryBody:
      'Rețin limba aleasă, păstrează înregistrarea în curs și salvează alegerea privind cookie-urile. Acestea nu pot fi dezactivate.',
    analyticsTitle: 'Analitice',
    analyticsBody:
      'Ne ajută să vedem ce pagini sunt folosite și unde se blochează vizitatorii, ca să le putem îmbunătăți.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Folosite pentru a măsura publicitatea noastră și a afișa conținut relevant pentru dumneavoastră.',
    preferencesTitle: 'Preferințe',
    preferencesBody:
      'Rețin opțiunile facultative pe care le alegeți, astfel încât site-ul să rămână așa cum l-ați lăsat.',
    colName: 'Nume',
    colPurpose: 'Scop',
    colExpiry: 'Expiră',
    localeCookie: 'Limba pe care ați ales-o pentru site.',
    consentCookie: 'Alegerile dumneavoastră privind cookie-urile.',
    expiry12m: '12 luni',
    expiry6m: '6 luni',
    signupState: 'Înregistrarea pe care o completați.',
    expiryStorage: 'Stocare locală, până la ștergere',
  },

  bg: {
    title: 'Използваме бисквитки',
    description:
      'Някои бисквитки са необходими, за да работи сайтът. С вашето съгласие бихме ги използвали и за да разберем как се използва сайтът и да измерим маркетинга си.',
    acceptAll: 'Приемам всички',
    rejectAll: 'Отхвърляне на несъществените',
    manage: 'Управление на настройките',
    prefTitle: 'Настройки за бисквитки',
    save: 'Запазване на настройките',
    close: 'Затваряне',
    introTitle: 'Как използваме бисквитките',
    introBody:
      'Необходимите бисквитки са винаги включени, защото без тях сайтът не работи. Всичко останало остава изключено, докато вие не го включите, и можете да промените избора си по всяко време.',
    necessaryTitle: 'Строго необходими',
    necessaryBody:
      'Запомнят избрания език, пазят започнатата регистрация и съхраняват избора ви за бисквитки. Не могат да бъдат изключени.',
    analyticsTitle: 'Аналитични',
    analyticsBody:
      'Помагат ни да видим кои страници се използват и къде посетителите се затрудняват, за да ги подобрим.',
    marketingTitle: 'Маркетингови',
    marketingBody:
      'Използват се, за да измерваме рекламата си и да показваме съдържание, което е подходящо за вас.',
    preferencesTitle: 'Предпочитания',
    preferencesBody:
      'Запомнят незадължителните ви избори, за да работи сайтът така, както сте го оставили.',
    colName: 'Име',
    colPurpose: 'Цел',
    colExpiry: 'Срок',
    localeCookie: 'Езикът, който сте избрали за сайта.',
    consentCookie: 'Вашият избор за бисквитките.',
    expiry12m: '12 месеца',
    expiry6m: '6 месеца',
    signupState: 'Регистрацията, която попълвате.',
    expiryStorage: 'Локално хранилище, докато не го изчистите',
  },

  tr: {
    title: 'Çerez kullanıyoruz',
    description:
      'Bazı çerezler sitenin çalışması için gereklidir. İzniniz olursa bunları sitenin nasıl kullanıldığını anlamak ve pazarlamamızı ölçmek için de kullanırdık.',
    acceptAll: 'Tümünü kabul et',
    rejectAll: 'Zorunlu olmayanları reddet',
    manage: 'Tercihleri yönet',
    prefTitle: 'Çerez tercihleri',
    save: 'Tercihleri kaydet',
    close: 'Kapat',
    introTitle: 'Çerezleri nasıl kullanıyoruz',
    introBody:
      'Gerekli çerezler her zaman açıktır, çünkü site onlarsız çalışmaz. Diğer her şey siz açana kadar kapalı kalır ve seçiminizi istediğiniz zaman değiştirebilirsiniz.',
    necessaryTitle: 'Kesinlikle gerekli',
    necessaryBody:
      'Seçtiğiniz dili hatırlar, sürmekte olan kaydınızı saklar ve çerez tercihinizi tutar. Bunlar kapatılamaz.',
    analyticsTitle: 'Analitik',
    analyticsBody:
      'Hangi sayfaların kullanıldığını ve ziyaretçilerin nerede takıldığını görmemize yardımcı olur, böylece onları iyileştirebiliriz.',
    marketingTitle: 'Pazarlama',
    marketingBody:
      'Reklamlarımızı ölçmek ve sizinle ilgili içerik göstermek için kullanılır.',
    preferencesTitle: 'Tercihler',
    preferencesBody:
      'Yaptığınız isteğe bağlı seçimleri hatırlar, böylece site bıraktığınız gibi davranır.',
    colName: 'Ad',
    colPurpose: 'Amaç',
    colExpiry: 'Süre',
    localeCookie: 'Site için seçtiğiniz dil.',
    consentCookie: 'Çerez tercihleriniz.',
    expiry12m: '12 ay',
    expiry6m: '6 ay',
    signupState: 'Tamamlamakta olduğunuz kayıt.',
    expiryStorage: 'Yerel depolama, siz silene kadar',
  },

  no: {
    title: 'Vi bruker informasjonskapsler',
    description:
      'Noen informasjonskapsler er nødvendige for at nettstedet skal fungere. Med ditt samtykke ville vi også brukt dem til å forstå hvordan nettstedet brukes og til å måle markedsføringen vår.',
    acceptAll: 'Godta alle',
    rejectAll: 'Avvis ikke-nødvendige',
    manage: 'Administrer innstillinger',
    prefTitle: 'Innstillinger for informasjonskapsler',
    save: 'Lagre innstillinger',
    close: 'Lukk',
    introTitle: 'Slik bruker vi informasjonskapsler',
    introBody:
      'Nødvendige informasjonskapsler er alltid på, fordi nettstedet ikke fungerer uten dem. Alt annet er av til du slår det på, og du kan endre valget ditt når som helst.',
    necessaryTitle: 'Strengt nødvendige',
    necessaryBody:
      'Husker språket du valgte, tar vare på registreringen du holder på med, og lagrer valget ditt om informasjonskapsler. Disse kan ikke slås av.',
    analyticsTitle: 'Analyse',
    analyticsBody:
      'Hjelper oss å se hvilke sider som brukes og hvor besøkende stopper opp, slik at vi kan forbedre dem.',
    marketingTitle: 'Markedsføring',
    marketingBody:
      'Brukes til å måle annonseringen vår og vise innhold som er relevant for deg.',
    preferencesTitle: 'Preferanser',
    preferencesBody:
      'Husker valgfrie innstillinger, slik at nettstedet oppfører seg slik du forlot det.',
    colName: 'Navn',
    colPurpose: 'Formål',
    colExpiry: 'Utløper',
    localeCookie: 'Språket du valgte for nettstedet.',
    consentCookie: 'Valgene dine om informasjonskapsler.',
    expiry12m: '12 måneder',
    expiry6m: '6 måneder',
    signupState: 'Registreringen du holder på med.',
    expiryStorage: 'Lokal lagring, til du sletter den',
  },
};

/** The locale the banner falls back to when a path prefix has no copy above. */
export const CONSENT_FALLBACK_LANG = 'en';
